import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: STORAGE_BUCKET.
 *
 * Object-storage container shared by AWS S3, Azure Blob, GCP Cloud Storage,
 * Cloudflare R2, DigitalOcean Spaces. Sibling types STORAGE_VOLUME (block)
 * and STORAGE_FILESYSTEM (file) are reserved — auditors ask different
 * questions of block + file storage, so the canonical layer keeps them
 * separate even though they share a vendor.
 *
 * What lives in canonical: the cross-provider compliance 80% — public
 * access, encryption-at-rest, lifecycle, object lock, versioning, tags.
 * Provider-specific knobs (S3 bucket key, R2 storage class differences,
 * Azure access tier, etc.) live on `providerExtensions` when a check
 * needs them, otherwise drop + `lossy: true`.
 *
 * NOT in canonical: object-level state (count, total bytes, sample
 * objects). The bucket *configuration* is what compliance checks evaluate;
 * object contents are not part of the resource layer.
 */

export const STORAGE_BUCKET_SCHEMA_VERSION = 1;

export const StorageBucket = z.object({
  /** Stable identifier surfaced to operators. Provider-specific format:
   *  S3 = ARN, R2 = `r2://<account>/<bucket>`, GCS = `gs://<bucket>`. */
  externalId: z.string(),

  /** Bucket name (without scheme/prefix). */
  name: z.string(),

  /** Region/location where the bucket lives. Null for providers that
   *  expose region only on object writes (some R2 plans) or auto-replicate. */
  region: z.string().nullable(),

  /** Bucket-level public access. */
  publicAccess: z.object({
    /** True when anything in the bucket is reachable without provider
     *  auth — via ACL, bucket policy, managed public subdomain, or
     *  custom public domain. */
    enabled: z.boolean(),
    /** Specific signals that triggered enabled=true. Values are short
     *  identifiers like `acl`, `bucket_policy`, `r2.dev_subdomain`,
     *  `custom_domain:foo.example.com`. Useful for the auditor pack's
     *  "why is this bucket public?" explanation. */
    sources: z.array(z.string()),
  }),

  /** Server-side encryption-at-rest. */
  encryptionAtRest: z.object({
    enabled: z.boolean(),
    /** `provider_managed` = vendor-default key (SSE-S3, R2 default,
     *  GCS Google-managed). `customer_managed` = customer-controlled
     *  KMS key. `unknown` when the projection can't tell. */
    keyManagement: z.enum(['provider_managed', 'customer_managed', 'unknown']),
    /** ARN/URN of the customer-managed key when `keyManagement` is
     *  `customer_managed`. Null otherwise. */
    keyId: z.string().nullable(),
  }),

  /** Lifecycle / retention rules. The canonical view captures
   *  rule identity + enabled state only; per-rule conditions stay
   *  provider-specific (different vendors model age/version/tags
   *  incompatibly) and are reachable from rawNative. */
  lifecycleRules: z.array(
    z.object({
      id: z.string(),
      enabled: z.boolean(),
    }),
  ),

  /** WORM / object lock. */
  objectLock: z.object({
    enabled: z.boolean(),
    /** S3 + R2 both expose governance/compliance modes; null when
     *  lock is disabled or the provider exposes only a binary toggle. */
    mode: z.enum(['governance', 'compliance']).nullable(),
  }),

  /** Bucket versioning toggle. Distinct from lifecycle: versioning
   *  preserves prior object generations, lifecycle expires them. */
  versioning: z.boolean(),

  /** Resource tags. Empty object when the provider has no tags or
   *  the projector didn't fetch them (in which case set `lossy: true`). */
  tags: z.record(z.string()),

  /** Escape hatch for provider-specific fields that don't generalize
   *  across the canonical core. Convention:
   *  - Field is provider-specific AND a check needs it → goes here,
   *    `lossy: false`.
   *  - Field is provider-specific AND no check needs it → drop +
   *    `lossy: true`.
   *  See RFC-179 §5.3 for the full decision table. */
  providerExtensions: z.record(z.unknown()).optional(),
});

export type StorageBucket = z.infer<typeof StorageBucket>;
